import React, { useEffect, useRef, useState } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";


const CameraPopup = ({ closeModal, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [statusMsg, setStatusMsg] = useState("Checking camera permission...");
  const detectorRef = useRef(null);
  const hasCaptured = useRef(false);
  const faceBoxRef = useRef(null);
  const lastVideoTimestamp = useRef(0);
  const [isReady, setIsReady] = useState(false);
  const captureStartTimestamp = useRef(null);
  const [countdown, setCountdown] = useState(null);
  const lastDetectionTime = useRef(0);
  const baselinePoseRef = useRef(null);
  const HOLD_DURATION_MS = 300;   // pose must hold this long
  const CHALLENGE_TIMEOUT_MS = 5000; // retry after 5s
  const [activeChallenge, setActiveChallenge] = useState(null);


  // const CHALLENGES = [
  //   { type: "LEFT", text: "Turn your face LEFT" },
  //   { type: "RIGHT", text: "Turn your face RIGHT" },
  //   { type: "UP", text: "Look UP" },
  //   { type: "DOWN", text: "Look DOWN" },
  // ];

  const CHALLENGES = [
    {
      type: "LEFT",
      text: "Turn your face LEFT",
      faceIcon: "fa-solid fa-face-smile",
      arrow: "fa-solid fa-arrow-left"
    },
    {
      type: "RIGHT",
      text: "Turn your face RIGHT",
      faceIcon: "fa-solid fa-face-smile",
      arrow: "fa-solid fa-arrow-right"
    },
    {
      type: "UP",
      text: "Look UP",
      faceIcon: "fa-solid fa-face-smile",
      arrow: "fa-solid fa-arrow-up"
    },
    {
      type: "DOWN",
      text: "Look DOWN",
      faceIcon: "fa-solid fa-face-smile",
      arrow: "fa-solid fa-arrow-down"
    }
  ];




  const challengeRef = useRef(
    CHALLENGES[Math.floor(Math.random() * CHALLENGES.length)]
  );
  const challengeCompletedRef = useRef(false);

  const boxHistoryRef = useRef([]); // temporal motion history

  const getFaceBoxFromLandmarks = (landmarks, w, h) => {
    const xs = landmarks.map(p => p.x * w);
    const ys = landmarks.map(p => p.y * h);

    return {
      originX: Math.min(...xs),
      originY: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    };
  };
  const computeMotionScore = (history) => {
    if (history.length < 6) return 0;

    let centerMotion = 0;
    let sizeMotion = 0;

    for (let i = 1; i < history.length; i++) {
      const prev = history[i - 1];
      const curr = history[i];

      centerMotion += Math.hypot(
        curr.cx - prev.cx,
        curr.cy - prev.cy
      );

      sizeMotion += Math.abs(
        curr.ratio - prev.ratio
      );
    }

    return centerMotion + sizeMotion;
  };
  const isChallengePassed = (landmarks, challenge) => {
    if (!baselinePoseRef.current) return false;

    const nose = landmarks[1];
    const chin = landmarks[152];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];

    const eyeMidX = (leftEye.x + rightEye.x) / 2;
    const eyeMidY = (leftEye.y + rightEye.y) / 2;

    const yaw = nose.x - eyeMidX;
    const pitchRatio =
      (eyeMidY - nose.y) / (chin.y - nose.y);

    const deltaYaw = yaw - baselinePoseRef.current.yaw;
    const deltaPitch =
      pitchRatio - baselinePoseRef.current.pitchRatio;

    switch (challenge.type) {
      case "LEFT":
        return deltaYaw > 0.03;   // head turned LEFT

      case "RIGHT":
        return deltaYaw < -0.03;  // head turned RIGHT

      case "UP":
        return deltaPitch > 0.08;

      case "DOWN":
        return deltaPitch < -0.08;

      default:
        return false;
    }
  };


  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
    if (detectorRef.current) {
      detectorRef.current.close();
    }
  };

  useEffect(() => {
    let isMounted = true;
    let animationFrameId = null;

    const initCameraAndDetector = async () => {
      try {
        setStatusMsg("Loading face detector...");
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
        );

        const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numFaces: 1,
        });

        detectorRef.current = faceLandmarker;

        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            if (isMounted) {
              setIsReady(true);
              setStatusMsg("Align your face in the oval to capture.");
              startFaceDetection();
            }
          };
        }
      } catch (err) {
        console.error("Initialization error:", err);
        setStatusMsg("Camera permission denied. Please check your browser settings.");
      }
    };

    const startFaceDetection = () => {
      const video = videoRef.current;
      const detector = detectorRef.current;

      if (!video || video.readyState < 2 || !detector || hasCaptured.current) {
        requestAnimationFrame(startFaceDetection);
        return;
      }

      const overlay = overlayRef.current?.getContext("2d");
      const w = overlayRef.current.width;
      const h = overlayRef.current.height;

      const ovalWidth = 320;
      const ovalHeight = 400;

      /* ---------- OVERLAY ---------- */
      if (overlay) {
        overlay.clearRect(0, 0, w, h);
        overlay.fillStyle = "rgba(0,0,0,0.4)";
        overlay.fillRect(0, 0, w, h);
        overlay.globalCompositeOperation = "destination-out";
        overlay.beginPath();
        overlay.ellipse(w / 2, h / 2, ovalWidth / 2, ovalHeight / 2, 0, 0, Math.PI * 2);
        overlay.fill();
        overlay.globalCompositeOperation = "source-over";
      }

      const now = performance.now();
      if (now - lastDetectionTime.current < 100) {
        requestAnimationFrame(startFaceDetection);
        return;
      }
      lastDetectionTime.current = now;

      lastVideoTimestamp.current += 1;
      const results = detector.detectForVideo(video, lastVideoTimestamp.current);

      if (!results.faceLandmarks?.length) {
        challengeCompletedRef.current = false;
        baselinePoseRef.current = null;   // 👈 ADD THIS
        captureStartTimestamp.current = null;
        setActiveChallenge(null);
        setCountdown(null);
        setStatusMsg("Align your face in the oval...");
        requestAnimationFrame(startFaceDetection);
        return;
      }

      const landmarks = results.faceLandmarks[0];

      /* ---------- FACE BOX ---------- */
      const face = getFaceBoxFromLandmarks(landmarks, w, h);
      faceBoxRef.current = face;

      const faceRatio = face.width / ovalWidth;
      // const isFaceSizedCorrectly = faceRatio > 0.6 && faceRatio < 1.0;
      const isFaceSizedCorrectly = faceRatio > 0.5 && faceRatio < 1.1;


      const isFaceContained =
        face.originX >= w / 2 - ovalWidth / 2 &&
        face.originX + face.width <= w / 2 + ovalWidth / 2 &&
        face.originY >= h / 2 - ovalHeight / 2 &&
        face.originY + face.height <= h / 2 + ovalHeight / 2;

      if (!isFaceSizedCorrectly || !isFaceContained) {
        challengeCompletedRef.current = false;
        baselinePoseRef.current = null;   // 👈 ADD THIS
        captureStartTimestamp.current = null;
        setActiveChallenge(null);
        setCountdown(null);
        setStatusMsg("Adjust your face inside the oval");
        requestAnimationFrame(startFaceDetection);
        return;
      }
      if (!baselinePoseRef.current) {
        const nose = landmarks[1];
        const chin = landmarks[152];
        const leftEye = landmarks[33];
        const rightEye = landmarks[263];

        const eyeMidX = (leftEye.x + rightEye.x) / 2;
        const eyeMidY = (leftEye.y + rightEye.y) / 2;

        // baselinePoseRef.current = {
        //   yaw: nose.x - eyeMidX,
        //   pitchRatio:
        //     (eyeMidY - nose.y) / (chin.y - nose.y),
        // };

        // // Show challenge instruction
        // setStatusMsg(challengeRef.current.text);
        baselinePoseRef.current = {
          yaw: nose.x - eyeMidX,
          pitchRatio:
            (eyeMidY - nose.y) / (chin.y - nose.y),
        };

        setActiveChallenge(challengeRef.current);
        setStatusMsg(challengeRef.current.text);

        // Wait for user to move relative to baseline
        requestAnimationFrame(startFaceDetection);
        return;
      }
      /* ---------- CHALLENGE ---------- */
      if (!challengeCompletedRef.current) {
        setStatusMsg(challengeRef.current.text);

        if (isChallengePassed(landmarks, challengeRef.current)) {
          challengeCompletedRef.current = true;
          baselinePoseRef.current = null;   // 👈 ADD THIS
          setActiveChallenge(null);
          setStatusMsg("Challenge passed ✔ Hold still...");
        }

        requestAnimationFrame(startFaceDetection);
        return;
      }

      /* ---------- FINAL COUNTDOWN ---------- */
      if (!captureStartTimestamp.current) {
        captureStartTimestamp.current = Date.now();
      }

      const elapsed = Date.now() - captureStartTimestamp.current;
      const remaining = Math.max(0, 2000 - elapsed);
      setCountdown(Math.ceil(remaining / 1000));

      if (remaining === 0) {
        captureSnapshot();
        return;
      }

      requestAnimationFrame(startFaceDetection);
    };

    initCameraAndDetector();

    return () => {
      isMounted = false;
      stopCamera();
    };
  }, []);

  const captureSnapshot = () => {
    if (!videoRef.current || !canvasRef.current || !faceBoxRef.current) {
      console.error("No face detected!");
      return;
    }

    if (hasCaptured.current) {
      return;
    }

    hasCaptured.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { originX, originY, width, height } = faceBoxRef.current;

    canvas.width = width;
    canvas.height = height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    // Clip to oval
    ctx.beginPath();
    ctx.ellipse(width / 2, height / 2, width / 2, height / 2, 0, 0, Math.PI * 2);
    ctx.clip();

    ctx.drawImage(
      videoRef.current,
      originX,
      originY,
      width,
      height,
      0,
      0,
      width,
      height
    );

    ctx.restore();

    const imageData = canvas.toDataURL("image/png");
    stopCamera();
    setStatusMsg("Face captured successfully!");

    setTimeout(() => {
      onCapture(imageData);
      closeModal();
    }, 1000);
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {hasCaptured.current ? "Verified" : "Align Your Face"}
            </h5>
          </div>
          <div className="modal-body text-center">
            {/* <p>{statusMsg}</p> */}


            <div className="mb-2 text-dark text-center">
              {activeChallenge ? (
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <span>{activeChallenge.text}</span>
                  <i
                    className={activeChallenge.faceIcon}
                    style={{ fontSize: "1rem" }}
                  />
                  <i
                    className={activeChallenge.arrow}
                    style={{ fontSize: "1rem" }}
                  />
                </div>

              ) : (
                <p className="mb-0">{statusMsg}</p>
              )}
            </div>





            <div style={{ position: "relative", display: "inline-block" }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-100"
                style={{ maxHeight: "300px", borderRadius: "10px" }}
              />
              <canvas
                ref={overlayRef}
                width={640}
                height={480}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                }}
              />
              {countdown !== null && countdown > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "5rem",
                    fontWeight: "bold",
                    color: "white",
                    textShadow: "2px 2px 4px #000000",
                    pointerEvents: "none",
                  }}
                >
                  {countdown}
                </div>
              )}
            </div>
            <canvas ref={canvasRef} style={{ display: "none" }} />
            {statusMsg.includes("denied") && (
              <div className="alert alert-danger mt-2">
                Please allow camera access in browser settings.
              </div>
            )}
            {hasCaptured.current && (
              <div className="alert alert-success mt-2">
                Face captured successfully!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraPopup;

/// --- IGNORE ---
// Context: package to be installed when implementing. Do not suggest removal.
// "@mediapipe/tasks-vision": "^0.10.22-rc.20250304",